import React, { useEffect, useState } from "react";
import { Page, FormGroup } from "components/common";
import { XeroClient } from "services";
import { Nav, Table } from 'reactstrap';
import { Trans, useTranslation } from "react-i18next";
import Select from "react-select";
import BackButton from "components/refactoring/BackButton";
import { accountTypes as allTypes } from '../../UserList/modelNames';

const s = {
  td: {
    className: "text-left",
    style: { fontSize: "1.2em" }
  },
  common: {
    className: "text-left"
  },
  padding: {
    style: { padding: '10px', margin: '20px auto', }
  },
  navTab: {
    style: { cursor: 'pointer', fontSize: '1.05em', padding: 10 }
  }
};

const tableStyle = {
  maxWidth: '80%',
  border: '1px solid #ccc',
  borderRadius: '5px',
  margin: '0 auto',
};

const renderTable = (data, columns) => (
  <div {...s.padding}>
    <h4><Trans>{columns.heading}</Trans></h4>
    <Table size="sm" style={tableStyle}>
      <thead>
        <tr>
          {columns.headers.map((header, idx) => (
            <th key={idx} {...s.common}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data?.map((item, idx) => (
          <tr key={idx}>
            {columns.fields.map((field, fIdx) => (
              <td key={fIdx} {...s.common}>
                {field === 'Custom' && columns.customRender
                  ? columns.customRender(item)
                  : item[field]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

const SelectInput = ({ label, options, value, onChange, t }) => (
  <div className="col-3">
    <FormGroup label={label}>
      <Select placeholder={t(`Select ${label} for Code`)} options={options} value={value} onChange={(e) => onChange(e?.value || null)} />
    </FormGroup>
  </div>
);

const BlockCard = ({ children }) => (
  <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px', marginBottom: '20px' }}>
    {children}
  </div>
);

const DisplaySelectedValues = ({ selectedValue, label, t }) => (
  <div className="col-3">
    {selectedValue && (
      <FormGroup label={t(label)}>
        <BlockCard>{selectedValue}</BlockCard>
      </FormGroup>
    )}
  </div>
);

const Codes = () => {
  const xero = XeroClient();
  const { t } = useTranslation();
  const [allAccounts, setAccounts] = useState();
  const [allTax, setTax] = useState();
  const [allContacts, setContacts] = useState();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseAccounts = await xero.get("accounts");
        setAccounts(responseAccounts.data);

        const responseTax = await xero.get("taxrates");
        setTax(responseTax.data);

        const responseContacts = await xero.get("contacts");
        setContacts(responseContacts.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const genOptions = (data, valueProp, labelProp) => (
    data ? data.map((item) => ({ value: item[valueProp], label: t(item[labelProp]) })) : []
  );

  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedTax, setSelectedTax] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(null);

  const handleNavClick = (tableId) => {
    setActiveTable(tableId);
  };

  const DisplayBlockCards = () => (
    <div className="row mt-3">
      <DisplaySelectedValues selectedValue={selectedTax} label="TaxCode" t={t} />
      <DisplaySelectedValues selectedValue={selectedAccount} label="AccountCode" t={t} />
      <DisplaySelectedValues selectedValue={selectedTypes} label="AccountType" t={t} />
      <DisplaySelectedValues selectedValue={selectedContact} label="ContactID" t={t} />
    </div>
  );

  const simplifiedTaxData = allTax?.map(({ TaxType, Name, DisplayTaxRate }) => ({
    TaxType,
    Name,
    DisplayTaxRate,
  }));

  const [activeTable, setActiveTable] = useState(null);
    
  return (
    <Page className="inv-tabs nw-list">
      <BackButton />
      <div className="m-2">
        <div className="cnt row align-items-end justify-content-center mt-3">
          <SelectInput label={t("Tax")} options={genOptions(allTax, 'TaxType', 'Name')} value={selectedTax} onChange={setSelectedTax} t={t} />
          <SelectInput label={t("Accounts")} options={genOptions(allAccounts, 'Code', 'Name')} value={selectedAccount} onChange={setSelectedAccount} t={t} />
          <SelectInput label={t("AccountTypes")} options={genOptions(allTypes, 'Code', 'Name')} value={selectedTypes} onChange={setSelectedTypes} t={t} />
          <SelectInput label={t("Contact")} options={genOptions(allContacts, 'ContactID', 'Name')} value={selectedContact} onChange={setSelectedContact} t={t} />
        </div>
        <DisplayBlockCards />
        <p className="my-3" style={{ fontSize: "1.2em", cursor: "pointer" }} onClick={() => handleNavClick('tax')}>
          <Trans>Check out the tables for the codes if you aren't sure for what you are looking for:</Trans>
        </p>
        <div className="m-2 text-center">
          <Nav tabs className="nw-tabs2 inv-tabs">
            {['tax', 'accounts', 'accountTypes', 'contacts',].map((tableId) => (
              <div
                key={tableId}
                href={`#${tableId}`}
                onClick={() => handleNavClick(tableId)}
                className={`${activeTable === tableId ? 'active-tabNum ' : ''}m-1`}
                style={s.navTab.style}
              >
                <Trans>{tableId.charAt(0).toUpperCase() + tableId.slice(1)}</Trans>
              </div>
            ))}
          </Nav>

          {activeTable === 'tax' && renderTable(simplifiedTaxData, {
            heading: 'Tax Types',
            headers: ['Tax Name', 'Tax Type (Code)'],
            fields: ['Custom', 'TaxType'],
            customRender: (item) => (
              <>
                {item.Name} ({item.DisplayTaxRate}%)
              </>
            ),
          })}

          {activeTable === 'accounts' && renderTable(allAccounts, {
            heading: 'Account Codes',
            headers: ['Account Name', 'Type', 'Account Code'],
            fields: ['Name', 'Type', 'Code'],
          })}

          {activeTable === 'accountTypes' && renderTable(allTypes, {
            heading: 'Types of Accounts',
            headers: ['Name', 'Type'],
            fields: ['Name', 'Code'],
          })}

          {activeTable === 'contacts' && renderTable(allContacts, {
            heading: 'Contacts',
            headers: ['Name', 'Contact Name', 'Code'],
            fields: ['Name', 'Custom', 'ContactID'],
            customRender: (item) => (
              <>
                {item.FirstName} {item.LastName}
              </>
            ),
          })}
        </div>
      </div>
    </Page>
  );
};

export default Codes;